import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import type { Exercise } from '../types/exercise'
import type { ExerciseTemplate } from '../types/exercise-template'
import { playPhaseCue } from '../utils/timer-audio'
import { acquireWakeLock, releaseWakeLock } from '../utils/wake-lock'
import { cancelTimerNotifications, scheduleTimerNotifications, type TimerNotificationEvent } from '../utils/timer-sw-bridge'
import {
  getTimerNotificationsEnabled,
  subscribeToTimerNotifications,
} from '../utils/timer-notifications-preference'
import i18n from '../i18n'

export type TimerPhase =
  | 'idle'
  | 'armed'
  | 'prep'
  | 'work'
  | 'side_change'
  | 'rest'
  | 'set_rest'
  | 'between_exercises'
  | 'completed'

export type Side = 'A' | 'B' | null

export const PREP_DURATION_MS = 5_000

interface SessionMeta {
  workoutId: string
  exercise: Exercise | null
  templates: ExerciseTemplate[]
}

interface TimerSnapshot {
  templateIdx: number
  setIdx: number
  repIdx: number
  phase: TimerPhase
  phaseStartedAt: number | null
  phaseDurationMs: number | null
  /** Phase to enter when `prep` ends. */
  phasePending: PendingPhase | null
  prewarned: boolean
  /** Last second already played as a 3-2-1 countdown tick (3, 2, or 1). */
  lastCountdownTick: number | null
  paused: boolean
  pausedRemainingMs: number | null
  /** Keys `${templateIdx}-${setIdx}-${repIdx}` of reps whose work has been performed. */
  completedReps: string[]
  endModalOpen: boolean
  resumePromptOpen: boolean
  /**
   * For alternate-side templates, tracks which side the current work bout is on.
   * `'A'` before/during the first side, `'B'` after the side change, `null`
   * for non-alternating templates.
   */
  currentSide: Side
}

interface DialogUiState {
  dialogOpen: boolean
}

export interface TimerState extends SessionMeta, TimerSnapshot, DialogUiState {}

interface PendingPhase {
  phase: 'work' | 'side_change' | 'rest' | 'set_rest'
  durationMs: number | null
  templateIdx: number
  setIdx: number
  repIdx: number
  side: Side
}

interface ArmParams {
  workoutId: string
  exercise: Exercise
  templates: ExerciseTemplate[]
  startAtTemplate?: number
}

interface ArmTemplateParams {
  workoutId: string
  exercise: Exercise
  template: ExerciseTemplate
}

interface TimerActions {
  armWorkout: (params: ArmParams) => void
  armTemplate: (params: ArmTemplateParams) => void
  /** From `armed` or paused state → enters `prep` for {@link PREP_DURATION_MS}. */
  startTimer: () => void
  togglePause: () => void
  manualAdvance: () => void
  advanceRep: () => void
  rewindRep: () => void
  advanceSet: () => void
  rewindSet: () => void
  tick: () => void
  recompute: () => void
  openDialog: () => void
  closeDialog: () => void
  openEndModal: () => void
  closeEndModal: () => void
  /** Discard the in-progress session entirely (no result saved). */
  discard: () => void
  /** Internal reset after successful save. */
  reset: () => void
  resumeAfterPrompt: () => void
  resetAfterPrompt: () => void
}

export type TimerStore = TimerState & TimerActions

const STORAGE_KEY = 'climb_log.timer_state.v1'
const PREWARN_THRESHOLD_MS = 30_000

const EMPTY_EXERCISE: Exercise = {
  id: '',
  title: '',
  description: null,
  duration_minutes: null,
  image_url: null,
  family: null,
  variant_label: null,
  equipment: null,
  category: { id: '', name: '', position: 0 },
}

const INITIAL_STATE: TimerState = {
  workoutId: '',
  exercise: EMPTY_EXERCISE,
  templates: [],
  templateIdx: 0,
  setIdx: 0,
  repIdx: 0,
  phase: 'idle',
  phaseStartedAt: null,
  phaseDurationMs: null,
  phasePending: null,
  prewarned: false,
  lastCountdownTick: null,
  paused: false,
  pausedRemainingMs: null,
  completedReps: [],
  endModalOpen: false,
  resumePromptOpen: false,
  dialogOpen: false,
  currentSide: null,
}

function repKey(templateIdx: number, setIdx: number, repIdx: number): string {
  return `${templateIdx}-${setIdx}-${repIdx}`
}

function workMs(template: ExerciseTemplate): number | null {
  if (template.manual) return null
  return template.work_seconds != null ? template.work_seconds * 1000 : null
}

interface NextPhase {
  templateIdx: number
  setIdx: number
  repIdx: number
  side: Side
  phase: 'work' | 'side_change' | 'rest' | 'set_rest' | 'between_exercises' | 'completed'
  phaseDurationMs: number | null
  cue: 'work_start' | 'rest_start' | 'complete' | null
}

function initialSideFor(template: ExerciseTemplate | undefined): Side {
  return template?.alternate_side ? 'A' : null
}

/**
 * Crossing out of one block and into `templateIdx`.
 *
 * The pause belongs to the block that just ended: `none` rolls straight into
 * the next work bout, `timed` counts a rest down and then does, and `manual`
 * parks in `between_exercises` with no duration until the athlete taps start —
 * which is what every crossing did before the pause was configurable, and so
 * is what a block with nothing stored still does.
 */
function crossIntoTemplate(templates: ExerciseTemplate[], templateIdx: number): NextPhase | null {
  const tpl = templates[templateIdx]
  if (!tpl) return null

  const previous = templates[templateIdx - 1]
  const kind = previous?.transition_kind ?? 'manual'
  const seconds = previous?.transition_seconds ?? 0
  const landing = { templateIdx, setIdx: 0, repIdx: 0, side: initialSideFor(tpl) }

  if (kind === 'none') {
    return { ...landing, phase: 'work', phaseDurationMs: workMs(tpl), cue: 'work_start' }
  }

  return {
    ...landing,
    phase: 'between_exercises',
    // A timed pause auto-advances like a rest; null is the open-ended wait.
    phaseDurationMs: kind === 'timed' && seconds > 0 ? seconds * 1000 : null,
    cue: 'rest_start',
  }
}

/** The last work bout of `templateIdx`, where rewinding into it lands. */
function lastWorkOfTemplate(templates: ExerciseTemplate[], templateIdx: number): NextPhase | null {
  const tpl = templates[templateIdx]
  if (!tpl) return null

  return {
    templateIdx,
    setIdx: Math.max(0, tpl.sets - 1),
    repIdx: Math.max(0, tpl.reps_per_set - 1),
    side: tpl.alternate_side ? 'B' : null,
    phase: 'work',
    phaseDurationMs: workMs(tpl),
    cue: 'work_start',
  }
}

/** True when finishing the current `work` phase completes the rep (last side or non-alternating). */
function workCompletesRep(template: ExerciseTemplate | undefined, side: Side): boolean {
  if (!template?.alternate_side) return true
  return side === 'B'
}

function nextAfterWork(
  templates: ExerciseTemplate[],
  templateIdx: number,
  setIdx: number,
  repIdx: number,
  side: Side,
  options: { forceSkipRest?: boolean } = {},
): NextPhase | null {
  const tpl = templates[templateIdx]
  if (!tpl) return null

  // Alternate-side templates: finishing side A leads to side B (via side_change if configured).
  if (tpl.alternate_side && side === 'A') {
    const changeSeconds = tpl.side_change_seconds ?? 0
    if (!options.forceSkipRest && changeSeconds > 0) {
      return {
        templateIdx,
        setIdx,
        repIdx,
        side: 'A',
        phase: 'side_change',
        phaseDurationMs: changeSeconds * 1000,
        cue: 'rest_start',
      }
    }
    return {
      templateIdx,
      setIdx,
      repIdx,
      side: 'B',
      phase: 'work',
      phaseDurationMs: workMs(tpl),
      cue: 'work_start',
    }
  }

  // Rep is fully done (non-alternating, or side B of alternating).
  const moreRepsInSet = repIdx + 1 < tpl.reps_per_set
  if (moreRepsInSet) {
    if (!options.forceSkipRest && tpl.rest_seconds > 0) {
      return {
        templateIdx,
        setIdx,
        repIdx,
        side: initialSideFor(tpl),
        phase: 'rest',
        phaseDurationMs: tpl.rest_seconds * 1000,
        cue: 'rest_start',
      }
    }
    return {
      templateIdx,
      setIdx,
      repIdx: repIdx + 1,
      side: initialSideFor(tpl),
      phase: 'work',
      phaseDurationMs: workMs(tpl),
      cue: 'work_start',
    }
  }
  const moreSetsInTemplate = setIdx + 1 < tpl.sets
  if (moreSetsInTemplate) {
    if (!options.forceSkipRest && tpl.set_rest_seconds > 0) {
      return {
        templateIdx,
        setIdx,
        repIdx,
        side: initialSideFor(tpl),
        phase: 'set_rest',
        phaseDurationMs: tpl.set_rest_seconds * 1000,
        cue: 'rest_start',
      }
    }
    return {
      templateIdx,
      setIdx: setIdx + 1,
      repIdx: 0,
      side: initialSideFor(tpl),
      phase: 'work',
      phaseDurationMs: workMs(tpl),
      cue: 'work_start',
    }
  }
  const nextTplIdx = templateIdx + 1
  if (nextTplIdx >= templates.length) {
    return { templateIdx, setIdx, repIdx, side: null, phase: 'completed', phaseDurationMs: null, cue: 'complete' }
  }
  return crossIntoTemplate(templates, nextTplIdx)
}

function nextAfterSideChange(
  templates: ExerciseTemplate[],
  templateIdx: number,
  setIdx: number,
  repIdx: number,
): NextPhase | null {
  const tpl = templates[templateIdx]
  if (!tpl) return null
  return {
    templateIdx,
    setIdx,
    repIdx,
    side: 'B',
    phase: 'work',
    phaseDurationMs: workMs(tpl),
    cue: 'work_start',
  }
}

function nextAfterRest(
  templates: ExerciseTemplate[],
  templateIdx: number,
  setIdx: number,
  repIdx: number,
): NextPhase | null {
  const tpl = templates[templateIdx]
  if (!tpl) return null
  return {
    templateIdx,
    setIdx,
    repIdx: repIdx + 1,
    side: initialSideFor(tpl),
    phase: 'work',
    phaseDurationMs: workMs(tpl),
    cue: 'work_start',
  }
}

function nextAfterSetRest(
  templates: ExerciseTemplate[],
  templateIdx: number,
  setIdx: number,
): NextPhase | null {
  const tpl = templates[templateIdx]
  if (!tpl) return null
  if (setIdx + 1 < tpl.sets) {
    return {
      templateIdx,
      setIdx: setIdx + 1,
      repIdx: 0,
      side: initialSideFor(tpl),
      phase: 'work',
      phaseDurationMs: workMs(tpl),
      cue: 'work_start',
    }
  }
  // We were resting between exercises — cross over to the next template's first work.
  const nextTplIdx = templateIdx + 1
  if (nextTplIdx >= templates.length) {
    return { templateIdx, setIdx, repIdx: 0, side: null, phase: 'completed', phaseDurationMs: null, cue: 'complete' }
  }
  const nextTpl = templates[nextTplIdx]
  return {
    templateIdx: nextTplIdx,
    setIdx: 0,
    repIdx: 0,
    side: initialSideFor(nextTpl),
    phase: 'work',
    phaseDurationMs: workMs(nextTpl),
    cue: 'work_start',
  }
}

function persistableSnapshot(state: TimerState): Record<string, unknown> | null {
  if (state.phase === 'idle') return null
  return {
    workoutId: state.workoutId,
    exercise: state.exercise,
    templates: state.templates,
    templateIdx: state.templateIdx,
    setIdx: state.setIdx,
    repIdx: state.repIdx,
    phase: state.phase,
    phaseStartedAt: state.phaseStartedAt,
    phaseDurationMs: state.phaseDurationMs,
    phasePending: state.phasePending,
    prewarned: state.prewarned,
    lastCountdownTick: state.lastCountdownTick,
    paused: state.paused,
    pausedRemainingMs: state.pausedRemainingMs,
    completedReps: state.completedReps,
    currentSide: state.currentSide,
  }
}

function loadFromStorage(): Partial<TimerState> | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as Partial<TimerState>
  } catch {
    return null
  }
}

function writeToStorage(state: TimerState): void {
  if (typeof window === 'undefined') return
  const snap = persistableSnapshot(state)
  try {
    if (snap) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snap))
    } else {
      window.localStorage.removeItem(STORAGE_KEY)
    }
  } catch {
    /* storage may be unavailable; non-fatal */
  }
}

function rehydrate(): Partial<TimerState> {
  const persisted = loadFromStorage()
  if (!persisted) return {}
  const tpl = persisted.templates?.[persisted.templateIdx ?? 0]
  const isManualWork = persisted.phase === 'work' && tpl?.manual === true
  return {
    ...persisted,
    paused: isManualWork ? true : Boolean(persisted.paused),
    resumePromptOpen: isManualWork,
    endModalOpen: false,
    dialogOpen: false,
  }
}

export const useTimerStore = create<TimerStore>()(
  subscribeWithSelector((set, get) => ({
    ...INITIAL_STATE,
    ...rehydrate(),

    armWorkout: ({ workoutId, exercise, templates, startAtTemplate }) => {
      const snapshot = templates.map((t) => ({ ...t }))
      if (snapshot.length === 0) return
      const startIdx = Math.min(Math.max(startAtTemplate ?? 0, 0), snapshot.length - 1)
      set({
        ...INITIAL_STATE,
        workoutId,
        exercise,
        templates: snapshot,
        templateIdx: startIdx,
        phase: 'armed',
        dialogOpen: true,
        currentSide: initialSideFor(snapshot[startIdx]),
      })
    },

    armTemplate: ({ workoutId, exercise, template }) => {
      get().armWorkout({ workoutId, exercise, templates: [template] })
    },

    startTimer: () => {
      const s = get()
      if (s.phase === 'armed' || s.phase === 'between_exercises') {
        const tpl = s.templates[s.templateIdx]
        if (!tpl) return
        enterPrep(set, {
          phase: 'work',
          durationMs: workMs(tpl),
          templateIdx: s.templateIdx,
          setIdx: s.setIdx,
          repIdx: s.repIdx,
          side: initialSideFor(tpl),
        })
        return
      }
      // Resume from a paused phase: prep first, then back to whatever was running.
      if (s.paused && s.pausedRemainingMs != null && s.phase !== 'idle' && s.phase !== 'completed') {
        const phase = s.phase
        if (phase === 'work' || phase === 'side_change' || phase === 'rest' || phase === 'set_rest') {
          enterPrep(set, {
            phase,
            durationMs: s.pausedRemainingMs,
            templateIdx: s.templateIdx,
            setIdx: s.setIdx,
            repIdx: s.repIdx,
            side: s.currentSide,
          })
        }
      }
    },

    togglePause: () => {
      const s = get()
      if (s.phase === 'idle' || s.phase === 'armed' || s.phase === 'completed') return
      if (s.phase === 'prep' || s.phase === 'between_exercises') return
      if (!s.paused) {
        if (s.phaseDurationMs == null) {
          set({ paused: true, pausedRemainingMs: null })
          return
        }
        const elapsed = s.phaseStartedAt != null ? Date.now() - s.phaseStartedAt : 0
        const remaining = Math.max(0, s.phaseDurationMs - elapsed)
        set({ paused: true, pausedRemainingMs: remaining })
      } else {
        // Resume → re-enter prep
        get().startTimer()
      }
    },

    tick: () => {
      const s = get()
      if (s.paused || s.phase === 'idle' || s.phase === 'armed' || s.phase === 'completed') return
      if (s.phaseDurationMs == null || s.phaseStartedAt == null) return
      const remaining = s.phaseDurationMs - (Date.now() - s.phaseStartedAt)

      // 30s prewarn — only for the phases that are a rest of some kind. A timed
      // transition is one; the open-ended wait never gets here (no duration).
      if (
        !s.prewarned &&
        (s.phase === 'rest' || s.phase === 'set_rest' || s.phase === 'between_exercises') &&
        remaining <= PREWARN_THRESHOLD_MS &&
        remaining > PREWARN_THRESHOLD_MS - 1000 &&
        remaining > 3_500
      ) {
        set({ prewarned: true })
        playPhaseCue('prewarn')
      }

      // 3-2-1 countdown — for any phase that leads into work or finishes
      const secondsLeft = Math.ceil(remaining / 1000)
      if (
        (s.phase === 'prep' ||
          s.phase === 'rest' ||
          s.phase === 'set_rest' ||
          s.phase === 'side_change' ||
          s.phase === 'between_exercises') &&
        secondsLeft >= 1 &&
        secondsLeft <= 3 &&
        (s.lastCountdownTick == null || secondsLeft < s.lastCountdownTick)
      ) {
        set({ lastCountdownTick: secondsLeft })
        if (secondsLeft === 3) playPhaseCue('countdown_3')
        else if (secondsLeft === 2) playPhaseCue('countdown_2')
        else playPhaseCue('countdown_1')
      }

      if (remaining <= 0) {
        autoAdvance(set, get, Date.now())
      }
    },

    manualAdvance: () => {
      const s = get()
      if (s.phase === 'work') {
        const tpl = s.templates[s.templateIdx]
        if (!tpl?.manual) return
        if (workCompletesRep(tpl, s.currentSide)) {
          markRepDone(set, s.templateIdx, s.setIdx, s.repIdx, s.completedReps)
        }
        const next = nextAfterWork(s.templates, s.templateIdx, s.setIdx, s.repIdx, s.currentSide)
        if (next) applyNext(set, next, Date.now())
      } else if (s.phase === 'side_change') {
        const next = nextAfterSideChange(s.templates, s.templateIdx, s.setIdx, s.repIdx)
        if (next) applyNext(set, next, Date.now())
      } else if (s.phase === 'rest') {
        const next = nextAfterRest(s.templates, s.templateIdx, s.setIdx, s.repIdx)
        if (next) applyNext(set, next, Date.now())
      } else if (s.phase === 'set_rest') {
        const next = nextAfterSetRest(s.templates, s.templateIdx, s.setIdx)
        if (next) applyNext(set, next, Date.now())
      }
    },

    advanceRep: () => {
      const s = get()
      if (s.phase === 'idle' || s.phase === 'armed' || s.phase === 'completed' || s.phase === 'prep') return
      if (s.phase === 'work') {
        const tpl = s.templates[s.templateIdx]
        if (workCompletesRep(tpl, s.currentSide)) {
          markRepDone(set, s.templateIdx, s.setIdx, s.repIdx, s.completedReps)
        }
      }
      const next = computeNextFor(s)
      if (!next) return
      applyNext(set, next, Date.now())
      if (next.phase === 'completed') set({ endModalOpen: true })
    },

    rewindRep: () => {
      const s = get()
      if (s.phase === 'idle' || s.phase === 'armed' || s.phase === 'completed' || s.phase === 'prep') return
      const tpl = s.templates[s.templateIdx]
      if (!tpl) return

      // between_exercises → back to the last work of the previous template
      if (s.phase === 'between_exercises') {
        if (s.templateIdx <= 0) return
        const back = lastWorkOfTemplate(s.templates, s.templateIdx - 1)
        if (back) applyNext(set, back, Date.now())
        return
      }

      // side_change → back to the work bout of side A in the same rep
      if (s.phase === 'side_change') {
        applyNext(
          set,
          {
            templateIdx: s.templateIdx,
            setIdx: s.setIdx,
            repIdx: s.repIdx,
            side: 'A',
            phase: 'work',
            phaseDurationMs: workMs(tpl),
            cue: 'work_start',
          },
          Date.now(),
        )
        return
      }

      // rest → back to the work bout that produced this rest
      if (s.phase === 'rest') {
        // For alternate-side templates, rest comes after side B, so step back to side B.
        const restoreSide: Side = tpl.alternate_side ? 'B' : null
        applyNext(
          set,
          {
            templateIdx: s.templateIdx,
            setIdx: s.setIdx,
            repIdx: s.repIdx,
            side: restoreSide,
            phase: 'work',
            phaseDurationMs: workMs(tpl),
            cue: 'work_start',
          },
          Date.now(),
        )
        return
      }

      // set_rest → back to the last work of the set we just finished
      if (s.phase === 'set_rest') {
        const restoreSide: Side = tpl.alternate_side ? 'B' : null
        applyNext(
          set,
          {
            templateIdx: s.templateIdx,
            setIdx: s.setIdx,
            repIdx: Math.max(0, tpl.reps_per_set - 1),
            side: restoreSide,
            phase: 'work',
            phaseDurationMs: workMs(tpl),
            cue: 'work_start',
          },
          Date.now(),
        )
        return
      }

      // work on side B → step back to the side change of the same rep
      // (or directly to the work of side A if no side change was configured)
      if (s.phase === 'work' && tpl.alternate_side && s.currentSide === 'B') {
        const changeSeconds = tpl.side_change_seconds ?? 0
        if (changeSeconds > 0) {
          applyNext(
            set,
            {
              templateIdx: s.templateIdx,
              setIdx: s.setIdx,
              repIdx: s.repIdx,
              side: 'A',
              phase: 'side_change',
              phaseDurationMs: changeSeconds * 1000,
              cue: 'rest_start',
            },
            Date.now(),
          )
        } else {
          applyNext(
            set,
            {
              templateIdx: s.templateIdx,
              setIdx: s.setIdx,
              repIdx: s.repIdx,
              side: 'A',
              phase: 'work',
              phaseDurationMs: workMs(tpl),
              cue: 'work_start',
            },
            Date.now(),
          )
        }
        return
      }

      // work (side A or non-alternating) → step back through the natural sequence
      // (rest of previous rep, or set_rest of previous set, or end of previous template)
      const sideAtPreviousRepEnd: Side = tpl.alternate_side ? 'B' : null
      if (s.repIdx > 0) {
        const phase: 'rest' | 'work' = tpl.rest_seconds > 0 ? 'rest' : 'work'
        applyNext(
          set,
          {
            templateIdx: s.templateIdx,
            setIdx: s.setIdx,
            repIdx: s.repIdx - 1,
            side: phase === 'rest' ? initialSideFor(tpl) : sideAtPreviousRepEnd,
            phase,
            phaseDurationMs: phase === 'rest' ? tpl.rest_seconds * 1000 : workMs(tpl),
            cue: phase === 'rest' ? 'rest_start' : 'work_start',
          },
          Date.now(),
        )
        return
      }
      if (s.setIdx > 0) {
        const phase: 'set_rest' | 'work' = tpl.set_rest_seconds > 0 ? 'set_rest' : 'work'
        applyNext(
          set,
          {
            templateIdx: s.templateIdx,
            setIdx: s.setIdx - 1,
            repIdx: Math.max(0, tpl.reps_per_set - 1),
            side: phase === 'set_rest' ? initialSideFor(tpl) : sideAtPreviousRepEnd,
            phase,
            phaseDurationMs: phase === 'set_rest' ? tpl.set_rest_seconds * 1000 : workMs(tpl),
            cue: phase === 'set_rest' ? 'rest_start' : 'work_start',
          },
          Date.now(),
        )
        return
      }
      if (s.templateIdx > 0) {
        // Rewind from the first work of a non-first block lands in the pause
        // that precedes it — or, when there is no pause, in the last work of
        // the block before, since that is what actually came first.
        const crossing = crossIntoTemplate(s.templates, s.templateIdx)
        const back =
          crossing?.phase === 'between_exercises'
            ? crossing
            : lastWorkOfTemplate(s.templates, s.templateIdx - 1)
        if (back) applyNext(set, back, Date.now())
      }
      // (else: at the very start; no-op)
    },

    advanceSet: () => {
      const s = get()
      if (s.phase === 'idle' || s.phase === 'armed' || s.phase === 'completed') return
      let templateIdx = s.templateIdx
      let setIdx = s.setIdx + 1
      const tpl = s.templates[templateIdx]
      if (!tpl) return
      if (setIdx >= tpl.sets) {
        templateIdx += 1
        setIdx = 0
      }
      if (templateIdx >= s.templates.length) {
        applyNext(
          set,
          { templateIdx: s.templateIdx, setIdx: s.setIdx, repIdx: s.repIdx, side: null, phase: 'completed', phaseDurationMs: null, cue: 'complete' },
          Date.now(),
        )
        set({ endModalOpen: true })
        return
      }
      const nextTpl = s.templates[templateIdx]
      applyNext(
        set,
        { templateIdx, setIdx, repIdx: 0, side: initialSideFor(nextTpl), phase: 'work', phaseDurationMs: workMs(nextTpl), cue: 'work_start' },
        Date.now(),
      )
    },

    rewindSet: () => {
      const s = get()
      if (s.phase === 'idle' || s.phase === 'armed' || s.phase === 'completed') return
      let templateIdx = s.templateIdx
      let setIdx = s.setIdx
      if (setIdx > 0) {
        setIdx -= 1
      } else if (templateIdx > 0) {
        templateIdx -= 1
        const prev = s.templates[templateIdx]
        setIdx = Math.max(0, (prev?.sets ?? 1) - 1)
      } else {
        return
      }
      const tpl = s.templates[templateIdx]
      if (!tpl) return
      applyNext(
        set,
        { templateIdx, setIdx, repIdx: 0, side: initialSideFor(tpl), phase: 'work', phaseDurationMs: workMs(tpl), cue: 'work_start' },
        Date.now(),
      )
    },

    recompute: () => {
      const s = get()
      if (s.paused || s.phase === 'idle' || s.phase === 'armed' || s.phase === 'completed') return
      if (s.phaseDurationMs == null || s.phaseStartedAt == null) return
      const now = Date.now()
      let cursor = s.phaseStartedAt + s.phaseDurationMs
      if (now < cursor) return
      let current = s
      while (now >= cursor) {
        const next = computeNextFor(current)
        if (!next) return
        // If we're leaving a work phase, mark its rep as completed only if it
        // actually closes the rep (last side or non-alternating template).
        if (current.phase === 'work') {
          const tpl = current.templates[current.templateIdx]
          if (workCompletesRep(tpl, current.currentSide)) {
            markRepDone(set, current.templateIdx, current.setIdx, current.repIdx, get().completedReps)
          }
        }
        applyNext(set, next, cursor, { skipCue: true })
        current = get()
        if (current.phase === 'idle' || current.phase === 'completed') {
          if (current.phase === 'completed') set({ endModalOpen: true })
          return
        }
        if (current.phase === 'work' && current.templates[current.templateIdx]?.manual) return
        if (current.phaseDurationMs == null) return
        cursor += current.phaseDurationMs
      }
    },

    openDialog: () => set({ dialogOpen: true }),
    closeDialog: () => set({ dialogOpen: false }),
    openEndModal: () => set({ endModalOpen: true, paused: true }),
    closeEndModal: () => set({ endModalOpen: false, paused: false, phaseStartedAt: Date.now() }),

    discard: () => set({ ...INITIAL_STATE }),
    reset: () => set({ ...INITIAL_STATE }),

    resumeAfterPrompt: () => {
      set({ resumePromptOpen: false, paused: false, phaseStartedAt: Date.now(), prewarned: false, lastCountdownTick: null })
    },

    resetAfterPrompt: () => set({ ...INITIAL_STATE }),
  })),
)

function applyNext(
  set: (partial: Partial<TimerState>) => void,
  next: NextPhase,
  startedAt: number,
  options: { skipCue?: boolean } = {},
): void {
  set({
    templateIdx: next.templateIdx,
    setIdx: next.setIdx,
    repIdx: next.repIdx,
    phase: next.phase,
    phaseStartedAt: next.phase === 'completed' ? null : startedAt,
    phaseDurationMs: next.phaseDurationMs,
    phasePending: null,
    prewarned: false,
    lastCountdownTick: null,
    currentSide: next.side,
  })
  if (!options.skipCue && next.cue) playPhaseCue(next.cue)
}

function autoAdvance(
  set: (partial: Partial<TimerState>) => void,
  get: () => TimerState,
  now: number,
): void {
  const s = get()
  // prep ending → apply phasePending
  if (s.phase === 'prep' && s.phasePending) {
    const pending = s.phasePending
    set({
      templateIdx: pending.templateIdx,
      setIdx: pending.setIdx,
      repIdx: pending.repIdx,
      phase: pending.phase,
      phaseStartedAt: now,
      phaseDurationMs: pending.durationMs,
      phasePending: null,
      prewarned: false,
      lastCountdownTick: null,
      paused: false,
      pausedRemainingMs: null,
      currentSide: pending.side,
    })
    playPhaseCue('work_start')
    return
  }
  if (s.phase === 'work') {
    const tpl = s.templates[s.templateIdx]
    if (workCompletesRep(tpl, s.currentSide)) {
      markRepDone(set, s.templateIdx, s.setIdx, s.repIdx, s.completedReps)
    }
  }
  const next = computeNextFor(s)
  if (!next) return
  applyNext(set, next, now)
  if (next.phase === 'completed') set({ endModalOpen: true })
}

function computeNextFor(s: TimerState): NextPhase | null {
  if (s.phase === 'work') return nextAfterWork(s.templates, s.templateIdx, s.setIdx, s.repIdx, s.currentSide)
  if (s.phase === 'side_change') return nextAfterSideChange(s.templates, s.templateIdx, s.setIdx, s.repIdx)
  if (s.phase === 'rest') return nextAfterRest(s.templates, s.templateIdx, s.setIdx, s.repIdx)
  if (s.phase === 'set_rest') return nextAfterSetRest(s.templates, s.templateIdx, s.setIdx)
  if (s.phase === 'between_exercises') {
    // `between_exercises` already sits on the new template at (0, 0). The work
    // is the first bout of that template. Used by `advanceRep` (skip the pause)
    // and by `getUpcomingWork`.
    const tpl = s.templates[s.templateIdx]
    if (!tpl) return null
    return {
      templateIdx: s.templateIdx,
      setIdx: 0,
      repIdx: 0,
      side: initialSideFor(tpl),
      phase: 'work',
      phaseDurationMs: workMs(tpl),
      cue: 'work_start',
    }
  }
  return null
}

export interface UpcomingWork {
  templateIdx: number
  setIdx: number
  repIdx: number
  /** Side of the upcoming work bout (for alternate-side templates), or null otherwise. */
  side: Side
  /** True when the upcoming work belongs to a different template than the current state. */
  isNewExercise: boolean
}

/**
 * Returns the upcoming `work` phase (skipping any rest/set_rest/side_change in
 * between), or `null` if the workout will complete next. Used by the timer UI
 * to preview what's coming after the current phase.
 */
export function getUpcomingWork(state: TimerState): UpcomingWork | null {
  if (state.phase === 'idle' || state.phase === 'armed' || state.phase === 'completed') {
    return null
  }
  if (state.phase === 'prep') {
    const pending = state.phasePending
    if (pending && pending.phase === 'work') {
      return {
        templateIdx: pending.templateIdx,
        setIdx: pending.setIdx,
        repIdx: pending.repIdx,
        side: pending.side,
        isNewExercise: false,
      }
    }
    return null
  }
  const baseTemplateIdx = state.templateIdx
  let cursor: { phase: TimerPhase; templateIdx: number; setIdx: number; repIdx: number; currentSide: Side } = {
    phase: state.phase,
    templateIdx: state.templateIdx,
    setIdx: state.setIdx,
    repIdx: state.repIdx,
    currentSide: state.currentSide,
  }
  let safety = 0
  while (safety++ < 20) {
    const synthetic = { ...state, ...cursor }
    const next = computeNextFor(synthetic)
    if (!next) return null
    if (next.phase === 'completed') return null
    if (next.phase === 'work') {
      return {
        templateIdx: next.templateIdx,
        setIdx: next.setIdx,
        repIdx: next.repIdx,
        side: next.side,
        isNewExercise: next.templateIdx !== baseTemplateIdx,
      }
    }
    cursor = {
      phase: next.phase,
      templateIdx: next.templateIdx,
      setIdx: next.setIdx,
      repIdx: next.repIdx,
      currentSide: next.side,
    }
  }
  return null
}

function enterPrep(set: (partial: Partial<TimerState>) => void, pending: PendingPhase): void {
  set({
    phase: 'prep',
    phaseStartedAt: Date.now(),
    phaseDurationMs: PREP_DURATION_MS,
    phasePending: pending,
    prewarned: false,
    lastCountdownTick: null,
    paused: false,
    pausedRemainingMs: null,
  })
}

function markRepDone(
  set: (partial: Partial<TimerState>) => void,
  templateIdx: number,
  setIdx: number,
  repIdx: number,
  completedReps: string[],
): void {
  const key = repKey(templateIdx, setIdx, repIdx)
  if (completedReps.includes(key)) return
  set({ completedReps: [...completedReps, key] })
}

/**
 * Build the list of background-Notification events for the current state.
 * Walks the phase sequence forward from now until a manual phase or the end
 * of the workout. Used by the visibility-hidden handler to hand off to the
 * Service Worker so the user gets vibration + notification sound when the
 * tab is backgrounded.
 *
 * Exported for testing.
 */
export function buildNotificationSchedule(
  state: TimerState,
  now: number,
  translate: (key: string, opts?: Record<string, unknown>) => string = (k) => k,
): TimerNotificationEvent[] {
  if (state.phase === 'idle' || state.phase === 'armed' || state.phase === 'completed') return []
  if (state.paused) return []
  if (state.phaseDurationMs == null || state.phaseStartedAt == null) return []

  const events: TimerNotificationEvent[] = []
  let phase: TimerPhase = state.phase
  let phaseStartedAt = state.phaseStartedAt
  let phaseDurationMs: number | null = state.phaseDurationMs
  let templateIdx = state.templateIdx
  let setIdx = state.setIdx
  let repIdx = state.repIdx
  let currentSide: Side = state.currentSide
  let phasePending = state.phasePending

  let safety = 0
  while (safety++ < 40 && phaseDurationMs != null) {
    const endAt = phaseStartedAt + phaseDurationMs

    let nextPhase: 'work' | 'side_change' | 'rest' | 'set_rest' | 'between_exercises' | 'completed' | null = null
    let nextDurationMs: number | null = null
    let nextTemplateIdx = templateIdx
    let nextSetIdx = setIdx
    let nextRepIdx = repIdx
    let nextSide: Side = currentSide

    if (phase === 'prep' && phasePending) {
      nextPhase = phasePending.phase
      nextDurationMs = phasePending.durationMs
      nextTemplateIdx = phasePending.templateIdx
      nextSetIdx = phasePending.setIdx
      nextRepIdx = phasePending.repIdx
      nextSide = phasePending.side
    } else {
      const synthetic: TimerState = {
        ...state,
        phase,
        templateIdx,
        setIdx,
        repIdx,
        currentSide,
        phaseStartedAt,
        phaseDurationMs,
        phasePending,
      }
      const next = computeNextFor(synthetic)
      if (!next) break
      nextPhase = next.phase
      nextDurationMs = next.phaseDurationMs
      nextTemplateIdx = next.templateIdx
      nextSetIdx = next.setIdx
      nextRepIdx = next.repIdx
      nextSide = next.side
    }

    const warnAt = endAt - 15_000
    if (warnAt > now) {
      events.push({
        id: `warn-${templateIdx}-${setIdx}-${repIdx}-${phase}-${endAt}`,
        at: warnAt,
        kind: 'prewarn_15s',
        title: translate('workoutTimer.notif.warn15.title', { defaultValue: 'Timer — next phase in 15s' }),
        body: translate('workoutTimer.notif.warn15.body', { defaultValue: 'Tap to return to the timer' }),
        vibrate: [80, 60, 80],
      })
    }

    if (endAt > now) {
      events.push({
        id: `change-${templateIdx}-${setIdx}-${repIdx}-${phase}-${endAt}`,
        at: endAt,
        kind: 'phase_change',
        title: translate(`workoutTimer.notif.${nextPhase}.title`, {
          defaultValue: 'Timer phase changed',
        }),
        body: bodyForEnteringPhase(state.templates, nextTemplateIdx, nextSetIdx, translate),
        vibrate: nextPhase === 'work' ? [220, 100, 220] : [220],
      })
    }

    if (nextPhase === 'completed' || nextPhase == null) break
    if (nextDurationMs == null) break

    phase = nextPhase
    phaseStartedAt = endAt
    phaseDurationMs = nextDurationMs
    templateIdx = nextTemplateIdx
    setIdx = nextSetIdx
    repIdx = nextRepIdx
    currentSide = nextSide
    phasePending = null

    if (events.length > 40) break
  }

  return events
}

function bodyForEnteringPhase(
  templates: ExerciseTemplate[],
  templateIdx: number,
  setIdx: number,
  translate: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const tpl = templates[templateIdx]
  const title = tpl?.title ?? translate('workoutTimer.untitledTemplate', { defaultValue: 'Template' })
  return translate('workoutTimer.notif.body', {
    title,
    set: setIdx + 1,
    total: tpl?.sets ?? 1,
    defaultValue: '{{title}} — Set {{set}}/{{total}}',
  })
}

// --- Side effects: persistence + ticker + visibility + wake lock + SW bridge ---

// Stable token identifying the timer as a holder of the shared screen wake lock.
const WAKE_LOCK_OWNER = Symbol('timer')

function isTimerRunning(s: TimerState): boolean {
  return s.phase !== 'idle' && s.phase !== 'armed' && s.phase !== 'completed' && !s.paused
}

function flushSchedule(): void {
  const state = useTimerStore.getState()
  if (!isTimerRunning(state) || !getTimerNotificationsEnabled()) {
    cancelTimerNotifications()
    return
  }
  const events = buildNotificationSchedule(state, Date.now(), (key, opts) => i18n.t(key, opts) as string)
  scheduleTimerNotifications(events)
}

if (typeof window !== 'undefined') {
  useTimerStore.subscribe(
    (state) => state,
    (state) => writeToStorage(state),
  )

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      cancelTimerNotifications()
      useTimerStore.getState().recompute()
      if (isTimerRunning(useTimerStore.getState())) {
        void acquireWakeLock(WAKE_LOCK_OWNER)
      }
    } else {
      flushSchedule()
    }
  })

  // React to the opt-in preference toggling at runtime: if the user enables it
  // while the tab is hidden mid-workout, schedule from now; if they disable it,
  // wipe any pending notifications.
  subscribeToTimerNotifications((enabled) => {
    if (!enabled) {
      cancelTimerNotifications()
      return
    }
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      flushSchedule()
    }
  })

  // Wake Lock follows the running state.
  useTimerStore.subscribe(
    (s) => isTimerRunning(s),
    (running) => {
      if (running) void acquireWakeLock(WAKE_LOCK_OWNER)
      else void releaseWakeLock(WAKE_LOCK_OWNER)
    },
    { fireImmediately: true },
  )

  let intervalId: number | null = null
  useTimerStore.subscribe(
    (s) => isTimerRunning(s),
    (running) => {
      if (running && intervalId === null) {
        intervalId = window.setInterval(() => useTimerStore.getState().tick(), 250)
      } else if (!running && intervalId !== null) {
        window.clearInterval(intervalId)
        intervalId = null
      }
    },
    { fireImmediately: true },
  )
}
